import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { useV2Account } from '../useV2Account';
import PatchPolicySection from './PatchPolicy';
import { STATES, rowsOf } from './patchValues';

const list = (v) => (Array.isArray(v) ? v : []);
export const fleetUrl = (account) => `cma/patchdriftfleet?${new URLSearchParams({ account }).toString()}`;

// What each kind of finding is called, how it is coloured and where it sorts. The words are the ones a customer reads.
const GAPS = {
  unknown: { label: 'Cannot tell', tone: 'none', rank: 0, meaning: 'Something this check needs could not be read from the machine, so it is not known whether it passes.' },
  unreachable: { label: 'Not reporting', tone: 'warn', rank: 1, meaning: 'The machine has not reported for longer than the limit, so what is shown about it may be out of date.' },
  updateServiceOff: { label: 'Updates switched off', tone: 'err', rank: 2, meaning: 'The Windows Update service is disabled, so nothing can install.' },
  installFailing: { label: 'Updates failing', tone: 'err', rank: 3, meaning: 'Windows updates fail to install.' },
  featureReleaseOld: { label: 'Windows too old', tone: 'err', rank: 4, meaning: 'This Windows release is older than the minimum release in the patching policy.' },
  updatesPaused: { label: 'Updates paused', tone: 'warn', rank: 5, meaning: 'Updates are paused on this machine, so patches are not being installed.' },
  deferralLong: { label: 'Updates held back', tone: 'warn', rank: 6, meaning: 'Quality updates are deferred for longer than the limit.' },
  releasePinned: { label: 'Release pinned', tone: 'warn', rank: 7, meaning: 'A Windows release is pinned by policy, so this machine will not move to a newer one.' },
  scanStale: { label: 'Not checking', tone: 'warn', rank: 8, meaning: 'Windows has not looked for updates within the limit.' },
  noRecentInstall: { label: 'Not patched', tone: 'err', rank: 9, meaning: 'No Windows update was installed within the limit.' },
  securityOverdue: { label: 'Security update overdue', tone: 'warn', rank: 10, meaning: 'No security update was installed within the limit.' },
  rebootPending: { label: 'Restart needed', tone: 'warn', rank: 11, meaning: 'A restart has been waiting for longer than the limit.' },
};
const gap = (g) => GAPS[g] || { label: g || '-', tone: 'none', rank: 99, meaning: '' };
const sorted = (findings) => [...list(findings)].sort((a, b) => gap(a.gap).rank - gap(b.gap).rank || String(a.name || a.key).localeCompare(String(b.name || b.key)));
const counts = (machine) => {
  const out = {};
  list(machine.findings).forEach((f) => { out[f.gap] = (out[f.gap] || 0) + 1; });
  return Object.entries(out).sort((a, b) => gap(a[0]).rank - gap(b[0]).rank);
};
export const ageText = (date, days) => {
  if (!date) return 'none found';
  if (days === 0) return `${date} (today)`;
  return days == null ? date : `${date} (${days} days ago)`;
};
// Each limit as a sentence, so the line under the findings reads as plain words. A limit that is switched off (0) says nothing: null.
export const LIMITS = {
  maxDaysSinceInstall: (n) => `Last update no older than ${n} days`,
  maxDaysSinceSecurity: (n) => `Last security update no older than ${n} days`,
  maxPendingRestartDays: (n) => `A restart may wait ${n} days`,
  maxFailures: (n) => (n === 1 ? '1 failed update allowed' : `${n} failed updates allowed`),
  minFeatureBuild: (n) => (n > 0 ? `Windows build ${n} or newer` : null),
  maxScanAgeDays: (n) => (n > 0 ? `Windows must have looked for updates within ${n} days` : null),
  maxReportAgeDays: (n) => (n > 0 ? `The machine must report within ${n} days` : null),
  maxQualityDeferDays: (n) => (n === 1 ? 'Quality updates may not be deferred' : n > 1 ? `Quality updates may be deferred up to ${n} days` : null),
  forbidPause: (n) => (n >= 1 ? 'Updates may not be paused' : null),
  forbidPinnedRelease: (n) => (n >= 1 ? 'The Windows release may not be pinned' : null),
};
const limitLine = (key, limits, from) => {
  const sentence = LIMITS[key](limits[key]);
  return sentence ? `${sentence} (${from[key] === 'default' ? 'default' : from[key]})` : null;
};
export const exceptionText = (e) => `${e.reason}. Approved by ${e.approvedBy}, until ${e.expires}${e.policy ? ` (${e.policy})` : ''}`;

// What holds updates back on a machine, in a few words for the table ("-" when nothing does; the detail has the rest).
export const heldBack = (control) => {
  if (!control) return <span className="v2-sub">not reported</span>;
  const parts = [];
  if (control.qualityPaused || control.featurePaused) parts.push('Paused');
  if (control.qualityDeferDays > 0) parts.push(`Deferred ${control.qualityDeferDays} d`);
  if (control.targetRelease) parts.push('Pinned');
  return parts.length ? parts.join(', ') : '-';
};

function MachineFindings({ machine }) {
  const findings = sorted(machine.findings);
  const name = machine.computer || machine.deviceId;
  const limits = machine.limits || {};
  const from = machine.limitSources || {};
  return (
    <div className="v2-detail">
      <div className="v2-detail-head"><h3>{`Checks of ${name}`}</h3></div>
      <table className="v2-table" aria-label="Checks">
        <thead><tr><th>Check</th><th>On this machine</th><th>Limit</th><th>Result</th></tr></thead>
        <tbody>
          {rowsOf(machine).map((r) => {
            const state = r.state === 'finding' ? { label: gap(r.gap).label, tone: gap(r.gap).tone } : STATES[r.state];
            return (
              <tr key={r.key} data-testid={`check-${r.key}`}>
                <td className="name">{r.label}</td>
                <td>{r.value}{r.sub && <div className="v2-sub">{r.sub}</div>}</td>
                <td>{r.limit}{r.source && r.limit !== 'Not checked' && <div className="v2-sub">{r.source === 'default' ? 'Default limit' : r.source}</div>}</td>
                <td><span className={`v2-pill ${state.tone}`} title={r.note || undefined}>{state.label}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {findings.length > 0 && <div className="v2-detail-head"><h3>Findings</h3></div>}
      <div className="v2-items">
        {findings.map((f, i) => (
          <div className="v2-patch-finding" key={`${f.key}-${i}`}>
            <div><span className={`v2-pill ${gap(f.gap).tone}`} title={gap(f.gap).meaning}>{gap(f.gap).label}</span></div>
            <div className="name">{f.name || f.key}</div>
            <div>{f.installed}</div>
            <div>{f.available ? `Limit: ${f.available}` : ''}<div className="v2-sub">{f.decidedBy === 'default' ? 'Default limit' : f.decidedBy}</div></div>
            <div>{f.note}</div>
          </div>
        ))}
      </div>
      <ul className="v2-limits" aria-label="Limits that apply">
        {Object.keys(LIMITS).filter((k) => limits[k] !== undefined && limitLine(k, limits, from)).map((k) => (
          <li key={k}>{limitLine(k, limits, from)}</li>
        ))}
      </ul>
      {list(machine.exceptions).length > 0 && (
        <ul className="v2-limits" aria-label="Exceptions that apply">
          {list(machine.exceptions).map((e, i) => <li key={i}>{`Exception: ${exceptionText(e)}`}</li>)}
        </ul>
      )}
    </div>
  );
}

// Patching, V2: each machine of an account against the patching limits that apply to it. It reads what the machine reports about its
// updates; it does not install anything. A machine opens to its findings (the others are hidden).
export default function PatchPage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const { accounts, account, choose, error: accountsError } = useV2Account();
  const [report, setReport] = useState({ state: 'idle' });
  const [open, setOpen] = useState(null);
  const code = account?.code;

  const load = useCallback(async () => {
    if (!code) return;
    setReport({ state: 'loading' });
    try {
      const response = await apiRef.current.request('GET', fleetUrl(code));
      setReport({ state: 'ok', body: response.data || {} });
    } catch (ex) {
      setReport({ state: 'error', error: errorText(ex) });
    }
  }, [code]);

  useEffect(() => { setOpen(null); load(); }, [load]);

  const body = report.state === 'ok' ? report.body : {};
  const machines = list(body.machines);
  const summary = body.summary || {};
  const idOf = (m) => m.deviceId || m.computer;

  return (
    <div>
      <div className="v2-head">
        <div>
          <h1>Patching</h1>
          <p>Is Windows being kept up to date? Each machine against the patching limits that apply to it.</p>
        </div>
        <div>
          <select className="v2-select v2-account" aria-label="Account" value={code || ''} onChange={(e) => choose(e.target.value)}>
            {accounts.map((a) => <option key={a.code} value={a.code}>{a.name ? `${a.code} - ${a.name}` : a.code}</option>)}
          </select>
        </div>
      </div>

      {accountsError && <div className="v2-alert err">{accountsError}</div>}
      {report.state === 'error' && <div className="v2-alert err">{report.error}</div>}
      {report.state === 'ok' && body.scheduled === false && <div className="v2-alert info">No machine of this account is set up to report its patching yet.</div>}
      {report.state === 'ok' && body.dataComplete === false && <div className="v2-alert info">Not every machine result could be read, so some machines may be missing.</div>}

      <section className="v2-card">
        {report.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
        {report.state === 'ok' && (
          <>
            <div className="v2-bar">
              <div>
                <span className="v2-pill none">{`${summary.machines || 0} machines`}</span>
                <span className={`v2-pill ${summary.withFindings ? 'warn' : 'ok'}`}>{`${summary.withFindings || 0} with findings`}</span>
                <span className="v2-pill none">{`${summary.findings || 0} findings`}</span>
              </div>
              {list(body.machinesWithoutResult).length > 0 && (
                <span className="v2-sub" title={body.machinesWithoutResult.join(', ')}>{`${body.machinesWithoutResult.length} ${body.machinesWithoutResult.length === 1 ? 'machine has' : 'machines have'} not reported yet`}</span>
              )}
            </div>
            {machines.length === 0 ? <div className="v2-empty">No machine of this account has reported its patching yet.</div> : (
              <table className="v2-table" aria-label="Patching by machine">
                <thead><tr><th style={{ width: 30 }} /><th>Machine</th><th>Findings</th><th>Last update</th><th>Last security update</th><th>Last scan</th><th>Held back</th><th>Reported</th></tr></thead>
                <tbody>
                  {machines.filter((m) => !open || open === idOf(m)).map((m) => {
                    const isOpen = open === idOf(m);
                    const chips = counts(m);
                    const patch = m.patch || {};
                    const toggle = () => setOpen(isOpen ? null : idOf(m));
                    return (
                      <React.Fragment key={idOf(m)}>
                        <tr className="row" aria-expanded={isOpen} tabIndex={0} onClick={toggle}
                          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(); } }}>
                          <td><span className="v2-caret" aria-hidden="true">&#9656;</span></td>
                          <td style={{ fontWeight: 700 }}>{m.computer || m.deviceId}<div className="v2-sub">{patch.os?.name ? `${patch.os.name} ${patch.os.release || ''} (${patch.os.build || ''})` : ''}</div></td>
                          <td>
                            {m.status === 'error' ? <span className="v2-pill err">{m.message || 'Failed'}</span>
                              : chips.length === 0 ? <span className="v2-pill ok">Patched</span>
                                : chips.map(([g, n]) => <span key={g} className={`v2-pill ${gap(g).tone}`} title={gap(g).meaning}>{`${gap(g).label}${n > 1 ? ` ${n}` : ''}`}</span>)}
                            {list(m.exceptions).length > 0 && <span className="v2-pill none" title={list(m.exceptions).map(exceptionText).join('\n')}>Exception</span>}
                          </td>
                          <td>{ageText(patch.lastInstall?.date, patch.daysSinceInstall)}</td>
                          <td>{ageText(patch.lastSecurityUpdate?.date, patch.daysSinceSecurity)}</td>
                          <td>{patch.scan ? ageText(patch.scan.lastSearchSuccess, patch.daysSinceScan) : <span className="v2-sub">not reported</span>}</td>
                          <td>{heldBack(patch.updateControl)}</td>
                          <td>{m.updatedUtc ? m.updatedUtc.slice(0, 16).replace('T', ' ') : ''}</td>
                        </tr>
                        {isOpen && <tr className="detail"><td colSpan={8}><MachineFindings machine={m} /></td></tr>}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>

      {code && <PatchPolicySection account={code} onChanged={load} />}
    </div>
  );
}
